import type { NskResponse } from "~/models/Api";
import type { DirectUploadedFile } from "~/models/DealForm";
import { isSuccess, postRequest } from "~/utils/api/api_handler";
import { generatePath } from "~/utils/generatePath";

export type DirectUploadAttachmentType =
	| "floor_map"
	| "lease_contract_file1"
	| "lease_contract_file2"
	| "lease_contract_file3"
	| "model_number_file";

type CreateParams = {
	attachmentType: DirectUploadAttachmentType;
	byteSize: number;
	contentType: string;
	originalFilename: string;
};

type CreateResponse = {
	uploadUrl: string;
	uploadUuid: string;
};

export const toDirectUploadedFileParams = (
	uploadedFile: DirectUploadedFile
) => ({
	byteSize: String(uploadedFile.file.size),
	contentType: uploadedFile.file.type || DEFAULT_CONTENT_TYPE,
	uploadUuid: uploadedFile.uploadUuid
});

export const create = async (
	params: CreateParams
): Promise<NskResponse<CreateResponse>> =>
	postRequest(generatePath("/direct_upload_files"), params);

export const update = async ({
	contentType,
	file,
	uploadUrl
}: {
	contentType: string;
	file: File;
	uploadUrl: string;
}): Promise<boolean> => {
	try {
		const response = await fetch(uploadUrl, {
			body: file,
			headers: { "Content-Type": contentType },
			method: "PUT"
		});
		return response.ok;
	} catch {
		return false;
	}
};

export const DEFAULT_CONTENT_TYPE = "application/octet-stream";
const DEFAULT_ERROR_MESSAGE = "ファイルのアップロードに失敗しました。";

export type UploadFileArg = {
	attachmentType: DirectUploadAttachmentType;
	file: File;
};

export const uploadFile = async ({
	attachmentType,
	file
}: UploadFileArg): Promise<DirectUploadedFile> => {
	const contentType = file.type || DEFAULT_CONTENT_TYPE;
	const response = await create({
		attachmentType,
		byteSize: file.size,
		contentType,
		originalFilename: file.name
	});
	if (!isSuccess(response)) {
		throw new Error(response.message ?? DEFAULT_ERROR_MESSAGE);
	}
	if (response.data === null) {
		throw new Error(DEFAULT_ERROR_MESSAGE);
	}
	const succeeded = await update({
		contentType,
		file,
		uploadUrl: response.data.uploadUrl
	});
	if (!succeeded) {
		throw new Error(DEFAULT_ERROR_MESSAGE);
	}
	return { file, uploadUuid: response.data.uploadUuid };
};
